import {useState, useRef, useMemo} from "react";

const escapeRegexChar = (char) => char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const useMentionAutocomplete = ({
    variables = [],
    value,
    onChange,
    wakeUpChar = '#',
    wrapMention = false,
    onMentionDelete,
    onVariableSelect,
    filterKey = 'display',
    selectKey = 'display',
    variableSets,
}) => {
    const sets = variableSets ?? [{wakeUpChar, variables, wrapMention, filterKey, selectKey, onMentionDelete, onVariableSelect}]

    const [query, setQuery] = useState("")
    const [showList, setShowList] = useState(false)
    const [cursorPos, setCursorPos] = useState(0)
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [activeSetIndex, setActiveSetIndex] = useState(0)
    const inputRef = useRef(null)

    const activeSet = sets[activeSetIndex]

    const filteredVariables = useMemo(() => {
        const fk = activeSet.filterKey ?? 'display'
        return (activeSet.variables ?? []).filter(v => v[fk].toLowerCase().includes(query.toLowerCase()))
    }, [sets, activeSetIndex, query])

    const getMentionSet = (text) => {
        const allMatches = new Set()
        for (const s of sets) {
            const ec = escapeRegexChar(s.wakeUpChar)
            const regex = s.wrapMention
                ? new RegExp(`\\{${ec}[\\w_]+\\}`, 'g')
                : new RegExp(`${ec}[\\w_]+`, 'g')
            const matches = text.match(regex) || []
            matches.forEach(m => allMatches.add(m))
        }
        return allMatches
    }

    const handleInputChange = (e) => {
        const val = e.target.value
        const start = e.target.selectionStart
        setCursorPos(start)

        if (sets.some(s => s.onMentionDelete)) {
            const oldMentions = getMentionSet(value)
            const newMentions = getMentionSet(val)
            oldMentions.forEach(m => {
                if (!newMentions.has(m)) {
                    for (const s of sets) {
                        if (!s.onMentionDelete) continue
                        const ec = escapeRegexChar(s.wakeUpChar)
                        const regex = s.wrapMention
                            ? new RegExp(`^\\{${ec}[\\w_]+\\}$`)
                            : new RegExp(`^${ec}[\\w_]+$`)
                        if (regex.test(m)) {
                            const varId = s.wrapMention ? m.slice(2, -1) : m.slice(1)
                            s.onMentionDelete(varId)
                            break
                        }
                    }
                }
            })
        }
        onChange(val)

        const textBefore = val.substring(0, start)
        let bestIndex = -1
        let bestSetIdx = 0
        for (let i = 0; i < sets.length; i++) {
            const lastWake = textBefore.lastIndexOf(sets[i].wakeUpChar)
            if (lastWake > bestIndex) {
                bestIndex = lastWake
                bestSetIdx = i
            }
        }

        if (bestIndex !== -1) {
            const currentQuery = textBefore.substring(bestIndex + 1)
            if (!currentQuery.includes(" ") && !currentQuery.includes("\n")) {
                setQuery(currentQuery)
                setShowList(true)
                setSelectedIndex(0)
                setActiveSetIndex(bestSetIdx)
                return
            }
        }
        setShowList(false)
    }

    const selectVariable = (variableId) => {
        const s = sets[activeSetIndex]
        const textBeforeAt = value.substring(0, value.lastIndexOf(s.wakeUpChar, cursorPos - 1))
        const textAfterCursor = value.substring(cursorPos)
        const token = s.wrapMention ? `{${s.wakeUpChar}${variableId}}` : `${s.wakeUpChar}${variableId}`
        const newValue = `${textBeforeAt}${token} ${textAfterCursor}`
        onChange(newValue)
        s.onVariableSelect?.(variableId)
        setShowList(false)
        setTimeout(() => inputRef.current?.focus(), 0)
    }

    const handleKeyDown = (e) => {
        if (showList && filteredVariables.length > 0) {
            if (e.key === "ArrowDown") {
                e.preventDefault()
                setSelectedIndex(p => (p + 1) % filteredVariables.length)
                return
            } else if (e.key === "ArrowUp") {
                e.preventDefault()
                setSelectedIndex(p => (p - 1 + filteredVariables.length) % filteredVariables.length)
                return
            } else if (e.key === "Enter") {
                e.preventDefault()
                const sk = activeSet.selectKey ?? 'display'
                selectVariable(filteredVariables[selectedIndex][sk])
                return
            }
        }

        if (e.key === "Backspace" && inputRef.current) {
            const start = inputRef.current.selectionStart
            const end = inputRef.current.selectionEnd
            if (start === end) {
                const textBeforeCursor = value.substring(0, start)
                for (const s of sets) {
                    const ec = escapeRegexChar(s.wakeUpChar)
                    const mentionRegex = s.wrapMention
                        ? new RegExp(`\\{${ec}[\\w_]+\\}$`)
                        : new RegExp(`${ec}[\\w_]+$`)
                    const mentionMatch = textBeforeCursor.match(mentionRegex)
                    if (mentionMatch) {
                        e.preventDefault()
                        const mentionText = mentionMatch[0]
                        const newText = value.substring(0, start - mentionText.length) + value.substring(start)
                        const varId = s.wrapMention ? mentionText.slice(2, -1) : mentionText.slice(1)
                        s.onMentionDelete?.(varId)
                        onChange(newText)
                        setShowList(false)
                        setTimeout(() => {
                            inputRef.current.setSelectionRange(start - mentionText.length, start - mentionText.length)
                        }, 0)
                        return
                    }
                }
            }
        }
    }

    const handleBlur = () => setTimeout(() => setShowList(false), 200)

    return {query, showList, selectedIndex, filteredVariables, cursorPos, handleInputChange, handleKeyDown, handleBlur, selectVariable, inputRef, activeSet}
}

export default useMentionAutocomplete;
